// ============================================================================
// Elicitation cards — interactive input widgets driven by the agent
// ============================================================================
// The agent sends ACP elicitation/create (form or url mode); the gateway
// fans out an "elicitation_request" WS frame. This module renders it as an
// inline card, collects the user's answer, and sends "elicitation_response"
// back. First responder wins across devices — other clients dismiss their
// card via "elicitation_resolved". Url-mode cards are marked complete when
// the agent sends elicitation/complete ("elicitation_completed" frame).
//
// Journaled replays of these requests render as read-only history cards —
// that path lives in message-rendering.js (renderElicitationReplay).

import { messagesEl } from "./dom.js";
import { addEl } from "./message-rendering.js";
import { buildFormFields, coerceFieldValue } from "./elicitation-schema.js";
import { icon as svgIcon, fileIcon as svgFileIcon } from "./icons.js";

// ---- deps (set by initElicitation) ----
let sendFn = null;

export function initElicitation(deps) {
  sendFn = deps.send;
  scrollDownFn = deps.scrollDown || scrollDownFn;
}

function el(tag, className, text) {
  const d = document.createElement(tag);
  if (className) d.className = className;
  if (text != null) d.textContent = text;
  return d;
}

let scrollDownFn = () => { messagesEl.scrollTop = messagesEl.scrollHeight; };

function scrollToBottom() {
  scrollDownFn();
}

// Option lists at or below this size render as a column of buttons (what
// the user actually taps); longer lists keep the compact dropdown/
// checkbox controls.
const OPT_BUTTON_MAX = 6;

/** Build the DOM control for one field descriptor. Returns {wrap, read}.
 *  opts.onPick(value) fires when a single-select option button is tapped —
 *  used to auto-submit one-question cards. */
function buildFieldControl(field, opts) {
  const wrap = el("div", "el-field");
  const label = el("label", "el-label", field.label + (field.required ? " *" : ""));
  label.htmlFor = `el-${field.name}`;
  wrap.appendChild(label);
  if (field.description) wrap.appendChild(el("div", "el-desc", field.description));

  let read;
  switch (field.kind) {
    case "select": {
      if (field.options.length <= OPT_BUTTON_MAX) {
        // One button per option — single-select picks one.
        const group = el("div", "el-opt-group");
        let picked = field.default != null ? String(field.default) : "";
        const btns = [];
        for (const o of field.options) {
          const b = el("button", "el-opt-btn", o.label);
          b.type = "button";
          if (o.value === picked) b.classList.add("picked");
          b.onclick = () => {
            picked = o.value;
            for (const x of btns) x.classList.toggle("picked", x === b);
            if (opts?.onPick) opts.onPick(o.value);
          };
          group.appendChild(b);
          btns.push(b);
        }
        wrap.appendChild(group);
        read = () => picked;
        break;
      }
      const sel = document.createElement("select");
      sel.className = "el-input";
      sel.id = `el-${field.name}`;
      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = "—";
      sel.appendChild(blank);
      for (const o of field.options) {
        const opt = document.createElement("option");
        opt.value = o.value;
        opt.textContent = o.label;
        sel.appendChild(opt);
      }
      if (field.default != null) sel.value = String(field.default);
      wrap.appendChild(sel);
      read = () => sel.value;
      break;
    }
    case "multiselect": {
      if (field.options.length <= OPT_BUTTON_MAX) {
        // Toggle buttons — tap each applicable option, then Submit.
        const group = el("div", "el-opt-group");
        const picked = new Set();
        for (const o of field.options) {
          const b = el("button", "el-opt-btn", o.label);
          b.type = "button";
          b.onclick = () => {
            if (picked.has(o.value)) { picked.delete(o.value); b.classList.remove("picked"); }
            else { picked.add(o.value); b.classList.add("picked"); }
          };
          group.appendChild(b);
        }
        wrap.appendChild(group);
        // Preserve declared option order in the response.
        read = () => field.options.filter((o) => picked.has(o.value)).map((o) => o.value);
        break;
      }
      const group = el("div", "el-check-group");
      const boxes = [];
      for (const o of field.options) {
        const row = el("label", "el-check-row");
        const cb = document.createElement("input");
        cb.type = "checkbox";
        cb.value = o.value;
        row.appendChild(cb);
        row.appendChild(el("span", null, o.label));
        group.appendChild(row);
        boxes.push(cb);
      }
      wrap.appendChild(group);
      read = () => boxes.filter((b) => b.checked).map((b) => b.value);
      break;
    }
    case "checkbox": {
      const row = el("label", "el-check-row");
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.id = `el-${field.name}`;
      if (field.default === true || field.default === "true") cb.checked = true;
      row.appendChild(cb);
      row.appendChild(el("span", null, "Yes"));
      wrap.appendChild(row);
      read = () => cb.checked;
      break;
    }
    case "textarea":
    case "textlist": {
      const ta = document.createElement("textarea");
      ta.className = "el-input el-textarea";
      ta.id = `el-${field.name}`;
      if (field.default != null) ta.value = String(field.default);
      if (field.kind === "textlist") ta.placeholder = "one per line, or comma-separated";
      wrap.appendChild(ta);
      read = () => ta.value;
      break;
    }
    case "number": {
      const inp = document.createElement("input");
      inp.type = "number";
      inp.className = "el-input";
      inp.id = `el-${field.name}`;
      if (field.min != null) inp.min = String(field.min);
      if (field.max != null) inp.max = String(field.max);
      if (field.step != null) inp.step = String(field.step);
      if (field.default != null) inp.value = String(field.default);
      wrap.appendChild(inp);
      read = () => inp.value;
      break;
    }
    default: {
      // text / password / url-ish
      const inp = document.createElement("input");
      inp.type = field.kind === "password" ? "password" : field.inputType || "text";
      inp.className = "el-input";
      inp.id = `el-${field.name}`;
      if (field.default != null) inp.value = String(field.default);
      wrap.appendChild(inp);
      read = () => inp.value;
    }
  }
  return { wrap, read };
}

function respond(card, msg, action, content) {
  sendFn({ type: "elicitation_response", id: msg.id, action, content });
  markCardResolved(card, action);
}

function markCardResolved(card, action) {
  card.classList.add(action === "accept" ? "resolved" : "rejected");
  for (const c of card.querySelectorAll("button, input, select, textarea")) {
    c.disabled = true;
  }
  const actions = card.querySelector(".pm-actions");
  if (actions) {
    const note = el("span", "el-state-note",
      action === "accept" ? "submitted" : action === "decline" ? "skipped" : "cancelled");
    actions.appendChild(note);
  }
}

/** Render a live elicitation_request as an interactive card. */
export function renderElicitation(msg) {
  // Dedupe against replayed journal rows: on session-switch reattach the
  // history replay renders the read-only "pending" card for the same
  // elicitationId — replace it with the live interactive card.
  if (msg.elicitationId) {
    for (const old of document.querySelectorAll(".elicit-card")) {
      if (old.dataset.elicitationId === msg.elicitationId && old.dataset.id !== msg.id) {
        old.remove();
      }
    }
  }
  const card = document.createElement("div");
  card.className = "perm-card elicit-card";
  card.dataset.id = msg.id;
  card.dataset.elicitationId = msg.elicitationId || "";

  const head = el("div", "pm-head");
  head.innerHTML = `<span class="pm-icon">${svgIcon(msg.mode === "url" ? "link" : "question")}</span><span class="pm-title"></span>`;
  head.querySelector(".pm-title").textContent = msg.message || "Input requested";
  card.appendChild(head);

  const actions = el("div", "pm-actions");

  if (msg.mode === "url") {
    // URL mode: user opens the link, does the external flow, then marks
    // done (or the agent's elicitation/complete closes it for them).
    if (msg.url && /^https?:\/\//i.test(msg.url)) {
      const a = el("a", "el-url", msg.url);
      a.href = msg.url;
      a.target = "_blank";
      a.rel = "noopener";
      card.appendChild(a);
    }
    const done = el("button", "pm-btn allow", "✓ Done");
    done.type = "button";
    done.onclick = () => respond(card, msg, "accept");
    const cancel = el("button", "pm-btn reject", "✕ Dismiss");
    cancel.type = "button";
    cancel.onclick = () => respond(card, msg, "cancel");
    actions.appendChild(done);
    actions.appendChild(cancel);
  } else {
    // Form mode: build fields from the requested JSON Schema.
    const fields = buildFormFields(msg.requestedSchema);
    const readers = [];
    const err = el("div", "el-error");
    // One-question cards: tapping an option button submits immediately —
    // the answer IS the pick (same as Devin's own ask_user_question UX).
    const singleSelect = fields.length === 1 && fields[0].kind === "select";
    for (const f of fields) {
      const { wrap, read } = buildFieldControl(f, {
        onPick: singleSelect ? () => doSubmit() : null,
      });
      let readCombined = read;
      if (f.allowCustom) {
        // "Other" free-text companion: replaces a select's value, or is
        // appended to a multiselect's array, when filled in.
        const other = el("input", "el-input");
        other.type = "text";
        other.placeholder = "Other…";
        wrap.appendChild(other);
        readCombined = () => {
          const base = read();
          const custom = other.value.trim();
          if (!custom) return base;
          return Array.isArray(base) ? [...base, custom] : custom;
        };
      }
      readers.push({ field: f, read: readCombined });
      card.appendChild(wrap);
    }
    card.appendChild(err);

    function doSubmit() {
      const content = {};
      const missing = [];
      for (const { field, read } of readers) {
        const raw = read();
        const empty = raw == null || raw === "" || (Array.isArray(raw) && raw.length === 0);
        if (field.required && empty) missing.push(field.label);
        const v = coerceFieldValue(field, raw);
        if (v !== undefined) content[field.name] = v;
      }
      if (missing.length) {
        err.textContent = "Required: " + missing.join(", ");
        return;
      }
      respond(card, msg, "accept", content);
    }
    const submit = el("button", "pm-btn allow", "✓ Submit");
    submit.type = "button";
    submit.onclick = doSubmit;
    const skip = el("button", "pm-btn bypass", "Not now");
    skip.type = "button";
    skip.onclick = () => respond(card, msg, "decline");
    const cancel = el("button", "pm-btn reject", "✕ Cancel");
    cancel.type = "button";
    cancel.onclick = () => respond(card, msg, "cancel");
    actions.appendChild(submit);
    actions.appendChild(skip);
    actions.appendChild(cancel);
  }

  card.appendChild(actions);
  addEl(card);
  scrollToBottom();
}

/** Another device resolved this elicitation — dismiss the local card. */
export function dismissElicitationCard(id, action) {
  for (const card of document.querySelectorAll(".elicit-card")) {
    if (card.dataset.id !== id) continue;
    markCardResolved(card, action);
  }
}

/** Agent sent elicitation/complete for a url-mode flow — mark the card. */
export function completeElicitationCard(elicitationId) {
  for (const card of document.querySelectorAll(".elicit-card")) {
    if (card.dataset.elicitationId !== elicitationId) continue;
    card.classList.add("resolved");
    for (const c of card.querySelectorAll("button, input, select, textarea")) {
      c.disabled = true;
    }
    const actions = card.querySelector(".pm-actions");
    if (actions && !actions.querySelector(".el-state-note")) {
      actions.appendChild(el("span", "el-state-note", "completed"));
    }
  }
}
